import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import ChatIcon from '../../assets/icons/chat.svg?react';
import EnlargeImgIcon from '../../assets/icons/enlarge-img.svg?react';
import ReduceImgIcon from '../../assets/icons/reduce-img.svg?react';
import ChangeCamIcon from '../../assets/icons/change-camera.svg?react';
import ShareLinkIcon from '../../assets/icons/share-link.svg?react';
import ShareScreenIcon from '../../assets/icons/share-screen.svg?react';
import NoAudioIcon from '../../assets/icons/vc-no-audio.svg?react';
import NoVideoIcon from '../../assets/icons/vc-no-video.svg?react';
import ActionsIcon from '../../assets/icons/actions.svg?react';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Column } from '../../components/Column/Column';
import { AudioControl } from '../../components/MeetControl/AudioControl';
import { CamControl } from '../../components/MeetControl/CamControl';
import { OptionsControl } from '../../components/MeetControl/OptionsControl';
import { NamedAvatar } from '../../components/NamedAvatar/NamedAvatar';
import { PopMenuButton } from '../../components/PopMenuButton/PopMenuButton';
import { Row } from '../../components/Row/Row';
import { SheetModal } from '../../components/SheetModal/SheetModal';
import { SheetModalHeader } from '../../components/SheetModal/SheetModalHeader';
import { Tip } from '../../components/Tip/Tip';
import {
	BottomShadow,
	ChangeCamContainer,
	ControlsContainer,
	EnlargeContainer,
	Guest,
	GuestContainer,
	ParticipantName,
	GuestPlaceholder,
	Host,
	HostCamDisabled,
	HostContainer,
	InfoTips,
	Logo,
	LogoWatermark,
	RoomContainer,
	RoomInfo,
	SharingScreenTip,
	ToolsContainer,
	ToolsSection,
} from './style';

import { Hidden } from '../../components/Hidden/Hidden';
import { useTranslationsContext } from '../../../features/Translations/providers/TranslationsContextProvider';
import { MeetDomainEvents, useMeetContext } from '../../../features/Meet';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useVideoCallContext } from '../../../features/VideoCall/providers/VideoCallContextProvider';
import { useRoom } from './useRoom';
import { ParticipantInfo } from '../../../features/ParticipantInfo/ParticipantInfo';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { OptionsList, Option } from '../../components/OptionsList/OptionsList';
import { useChatContext } from '../../../features/Chat/providers/ChatContextProvider';
import { Chat } from '../../../features/Chat/components/Chat/Chat';
import { ToolsContent } from '../../components/Tools/ToolsContent';
import { ToolsHeaderContent, ToolsHeaderSeparator, Separator, ToolsOptions, ToolsHeaderContainer, ToolItem, NotificationDot, ToolItemName, ToolIcon } from '../../components/Tools/style';
import { HangControl } from '../../components/MeetControl/HangControl';
import { HangUpModal } from './components/HangUpModal';
import { TemporalAlert } from '../../components/Alert/TemporalAlert';
import { publish, subscribe, unsubscribe } from '../../../features/EventsBus/services/service';
import { VideoCallEvents } from '../../../features/VideoCall/domain/events';
import { MeetTimer } from '../../components/MeetTimer/MeetTimer';
import { useParticipantInfo } from '../../../features/ParticipantInfo/hooks/useParticipantInfo';
import { Chip } from '../../components/Chip/Chip';

export interface RoomProps {
	code?: string;
	tenant?: string;
}

export function Room({ code, tenant }: Readonly<RoomProps>) {
	const { t } = useTranslationsContext();
	const theme = useTheme();
	const isMobile = useIsMobile();
	const [showProblemOccurredAlert, setShowProblemOccurredAlert] = useState(false);

	const isDesktop = useIsDesktop();
	const {
		showParticipantPlaceholder,
		participantMuted,
		participantHiddenCam,
		participantScreenSharing,
		isParticipantJoined,
	} = useVideoCallContext();

	const {
		meet,
		meetLink,
		isProfessional,
		participantData,
		professionalData,
		patientData,
		isCopiedMeetLinkToClipboard,
		copyMeetLinkToClipboard,
		closeMeetLinkCopiedAlert,
		getInsuranceBrand,
	} = useMeetContext();

	const {
		enlargedHostImage,
		publisherPublishing,
		publisherScreenSharing,
		openedMeetActions,
		hiddenCam,
		muted,
		hangUpDialog,
		toggleCamVisibility, 
		toggleHostImageSize,
		cycleVideo,
		startScreenSharing,
		stopScreenSharing,
		openMeetActions,
		closeMeetActions,
		toggleMic,
		toggleHangUpDialog,
		hangUp,
	} = useRoom(code, tenant);

	const { expanded, messages, notReaded, toggleChat,  } = useChatContext();
	const { startDate, duration } = useParticipantInfo();

	const tips = useMemo(() => {
		return (
			<InfoTips $toLeft={!isMobile && expanded}>
				<Column $gap={14}>
					{participantHiddenCam && (
						<Tip
							icon={<NoVideoIcon fill={theme.colors.brand.primary[300]} width={18} height={18} />}
							onlyIcon={!isMobile && expanded}
						>
							{participantData?.fullName} {t('alerts.participant_disabled_cam')}
						</Tip>
					)}
					{participantMuted && (
						<Tip
							icon={<NoAudioIcon fill={theme.colors.brand.primary[300]} width={20} height={20} />}
							onlyIcon={!isMobile && expanded}
						>
							{participantData?.fullName} {t('alerts.participant_disabled_mic')}
						</Tip>
					)}
				</Column>
			</InfoTips>
		);
	}, [participantHiddenCam, participantMuted, participantData, isMobile, expanded, t, theme]);

	const meetActions = useMemo((): Option[] => {
		const elements = [
			{
				key: 'copy_link',
				prepend: <ShareLinkIcon width={24} height={12} fill={theme.colors.brand.primary[300]} />,
				label: t('meet_actions.copy_link'),
				onClick: () => {
					copyGuestLinkToClipboard();
		            closeMeetActions();
				},
			},
		];

		if (!isMobile) {
			elements.push({
				key: 'share_screen',
				prepend: <ShareScreenIcon width={25} height={17} fill={theme.colors.brand.primary[300]} />,
				label: publisherScreenSharing
					? t('meet_actions.stop_share_screen')
					: t('meet_actions.share_screen'),
				onClick: () => {
					if (publisherScreenSharing) {
						stopScreenSharing();
					} else {
						startScreenSharing('screen-publisher');
					}
					closeMeetActions();
				},
			});
		}

		return elements;
	}, [isMobile, publisherScreenSharing, stopScreenSharing, startScreenSharing, closeMeetActions, t, theme]);

	const insuranceBrand = useMemo(() => {
		return meet?.origin ? getInsuranceBrand(meet.origin) : undefined;
	}, [meet]);

	const tools = useMemo(() => {
		return (
			<ToolsSection>
				<ToolsContainer>
					<ToolsHeaderContainer>
						<ToolsHeaderContent>
						{insuranceBrand?.name && (
							<Row $vcenter $gap={22}>
								<Chip label={insuranceBrand.name.toUpperCase()} $bgColor={insuranceBrand.secondary} $color={insuranceBrand.primary} data-testid="insurance-chip" />
								<Separator />
							</Row>
						)}
							<div>
								<ToolItem $isMobile={isMobile} $expanded={expanded} onClick={toggleChat}>
									<Row $gap={6} $align="left" $vcenter>
										<ToolIcon $expanded={expanded}>
											<ChatIcon fill={theme.colors.brand.primary[300]} width={18} height={16} />
											{!expanded && notReaded && <NotificationDot />}
										</ToolIcon>
										{!isMobile && <ToolItemName $bold={expanded}>{t('tools.chat')}</ToolItemName>}
									</Row>
								</ToolItem>
							</div>
							<Row $vcenter $gap={22}>
								{isDesktop && isProfessional && (
									<PopMenuButton
										opened={openedMeetActions}
										open={() => {
											openMeetActions();
										}}
										close={() => {
											closeMeetActions();
										}}
										popChildren={
											<OptionsList
												$expandDirection="bottom"
												options={meetActions}
											/>
										}
										$expandDirection="bottom"
									>
										<Column $fillHeight $vcenter>
											<ToolsOptions>
												<ActionsIcon fill="#AAAAAA" />
											</ToolsOptions>
										</Column>
									</PopMenuButton>
								)}
								<Separator />
								<MeetTimer startDate={startDate} duration={duration} />
							</Row>
						</ToolsHeaderContent>
					</ToolsHeaderContainer>
					{!isMobile && expanded && <ToolsHeaderSeparator />}
					{!isMobile && expanded && (
						<ToolsContent size={messages.length > 0 ? 75 : 40} expanded={expanded}>
							<Chat />
						</ToolsContent>
					)}
					{isMobile && (
						<SheetModal
							isOpen={expanded}
							header={
								<SheetModalHeader
									prepend={
										<ChatIcon fill={theme.colors.brand.primary[300]} width={18} height={16} />
									}
									title={t('tools.chat')}
								/>
							}
							heightPercentage={100}
							onClose={() => {
								toggleChat();
							}}
						>
							<Chat />
						</SheetModal>
					)}
				</ToolsContainer>
				{!isMobile && tips}
			</ToolsSection>
		);
	}, [expanded, isMobile, messages, notReaded, t, theme, isDesktop, openedMeetActions, meetActions, publisherScreenSharing, tips, startDate, duration]);

	const copyGuestLinkToClipboard = useCallback(() => {
		try {
			if (!meetLink) {
				return;
			}

			navigator.clipboard.writeText(meetLink);
			copyMeetLinkToClipboard(2000);
		} catch (e) {
			publish(MeetDomainEvents.copyMeetLinkToClipboardFailed, {
				error: e,
			});
		}
	}, [meetLink]);

	const redirectToWaitingRoom = useCallback(() => {
		const tenantParam = tenant ? `/tenant/${tenant}` : '';
		window.location.replace(`/c/${code}${tenantParam}`);
	}, [code, tenant]);

	const closeProblemOccurredAlertCallback = useCallback(() => {
		setShowProblemOccurredAlert(false);
		redirectToWaitingRoom();
	}, []);

	const openProblemOccurredAlertCallback = useCallback(() => {
		window.localStorage.clear();
		window.sessionStorage.clear();
		setShowProblemOccurredAlert(true);
	}, []);


	useEffect(() => {
		subscribe(VideoCallEvents.publisherCreationFailed, openProblemOccurredAlertCallback);
		return () => {
			unsubscribe(VideoCallEvents.publisherCreationFailed, openProblemOccurredAlertCallback);
		};
	}, []);

	return (
		<RoomContainer className={'meet-container'}>
			{isCopiedMeetLinkToClipboard && (
				<Alert
					type={'success'}
					message={t('alerts.copied_link')}
					onClose={() => {
						closeMeetLinkCopiedAlert();
					}}
				/>
			)}
			<GuestContainer>
				<LogoWatermark>
					<Logo src={theme.logo} />
				</LogoWatermark>
				{publisherScreenSharing && !isMobile && (
					<SharingScreenTip>
						<Tip
							icon={
								<ShareScreenIcon fill={theme.colors.brand.primary[300]} width={28} height={28} />
							}
							append={
								<Button
									$color={'primary'}
									$rounded
									onClick={() => {
										stopScreenSharing();
									}}
								>
									{t('meet_actions.stop_share_screen')}
								</Button>
							}
						>
							{t('alerts.sharing_screen')}
						</Tip>
					</SharingScreenTip>
				)}
				{participantScreenSharing && !isMobile && (
					<SharingScreenTip>
						<Tip
							icon={
								<ShareScreenIcon fill={theme.colors.brand.primary[300]} width={28} height={28} />
							}
						>
							{t('alerts.presentation')} {professionalData?.fullName}
						</Tip>
					</SharingScreenTip>
				)}

				{showParticipantPlaceholder && (
					<GuestPlaceholder>
						<RoomInfo>
							<Column $gap={34} $hcenter>
								{participantData && (
									<NamedAvatar
										name={participantData.fullName}
										size={isMobile ? 88 : 176}
									/>
								)}
								{!isParticipantJoined && (
									<ParticipantInfo
										$boldColor={theme.colors.neutral[100]}
										$textColor={theme.colors.neutral[100]}
									/>
								)}
								{participantHiddenCam && (
									<ParticipantName>
										{isProfessional ? patientData?.fullName : professionalData?.fullName}
									</ParticipantName>
								)}
							</Column>
						</RoomInfo>
					</GuestPlaceholder>
				)}
				<BottomShadow />
				<Guest
					id={'meet-guest'}
					hidden={(!isParticipantJoined && !participantHiddenCam) || participantScreenSharing}
				/>
				<Guest id={'screen-participant'} hidden={!participantScreenSharing} />
			</GuestContainer>
			<HostContainer $enlarged={enlargedHostImage} hidden={!publisherPublishing}>
				{!isMobile && (
					<EnlargeContainer onClick={toggleHostImageSize}>
						{!enlargedHostImage && <EnlargeImgIcon fill={theme.colors.neutral[100]} />}
						{enlargedHostImage && <ReduceImgIcon fill={theme.colors.neutral[100]} />}
					</EnlargeContainer>
				)}
				{isMobile && (
					<ChangeCamContainer onClick={cycleVideo}>
						<ChangeCamIcon fill={theme.colors.brand.primary[300]} />
					</ChangeCamContainer>
				)}
				<Host
					id={'meet-publisher'}
					hidden={publisherScreenSharing || hiddenCam}
				></Host>
				{hiddenCam && !publisherScreenSharing && (
					<HostCamDisabled>
						<NamedAvatar
							name={participantData?.fullName}
							size={92}
						/>
					</HostCamDisabled>
				)}
				<Host id={'screen-publisher'} hidden={!publisherScreenSharing}></Host>
			</HostContainer>
			{!!tools && tools}
			{isMobile && tips}
			<ControlsContainer>
				<Row $gap={18}>
					<AudioControl $off={muted} $withMenu onClick={toggleMic} />
					<CamControl $off={hiddenCam} $withMenu={isDesktop} onClick={toggleCamVisibility} />
					<HangControl data-testid="meet-room_hang-up" onClick={hangUp} />
					<Hidden hidden={!isProfessional || !isMobile}>
						<PopMenuButton
							opened={openedMeetActions}
							open={() => {
								openMeetActions();
							}}
							close={() => {
								closeMeetActions();
							}}
							popChildren={(
								<OptionsList
									$expandDirection="top"
									$horizontalDirection="right"
									options={meetActions}
								/>
							)}
							$expandDirection="top"
						>
							<OptionsControl />
						</PopMenuButton>
					</Hidden>
				</Row>
			</ControlsContainer>
			{isProfessional && <HangUpModal show={hangUpDialog} onClose={toggleHangUpDialog} />}
			<TemporalAlert show={showProblemOccurredAlert} type="error" message={t('common.problem_occurred')} timeInMs={3000} onClose={closeProblemOccurredAlertCallback} />
		</RoomContainer>
	);
}
